import { Slot } from '@radix-ui/react-slot'
import clsx from 'clsx'
import { motion } from 'motion/react'
import React, { type ComponentProps, forwardRef } from 'react'

import { ButtonGroup } from 'slab'

import { SupportLink } from './SupportLink'

function Header ({
  align = 'center',
  heading,
  subheading,
  subheadingAsChild,
}: {
  align?: 'left' | 'center' | 'right';
  heading: React.ReactNode;
  subheading?: React.ReactNode;
  subheadingAsChild?: boolean;
}) {
  const SubheadingComponent = subheadingAsChild ? Slot : 'p'

  return (
    <div
      className={clsx('mb-16', {
        'text-left': align === 'left',
        'text-center': align === 'center',
        'text-right': align === 'right',
      })}
    >
      <h1 className='heading-lg xs:heading-xl mb-6 text-balance'>{heading}</h1>
      {subheading && (
        <SubheadingComponent className='body xs:heading-sm-regular text-gray-500 text-balance'>
          {subheading}
        </SubheadingComponent>
      )}
    </div>
  )
}

Header.displayName = 'CenteredLayout.Header'

function Container ({
  children,
  padded = true,
  hideCard = false,
}: {
  children: React.ReactNode;
  padded?: boolean;
  hideCard?: boolean;
}) {
  return (
    <div
      className={clsx(
        'space-y-6',
        {
          'rounded-2xl border border-gray-200 shadow mb-10 bg-white': !hideCard,
          'p-6': padded,
        }
      )}
    >
      {children}
    </div>
  )
}

Container.displayName = 'CenteredLayout.Container'

const Buttons = forwardRef<HTMLDivElement, ComponentProps<typeof ButtonGroup>>(({
  align = 'center',
  children,
  ...props
}, forwardedRef) => {
  return (
    <ButtonGroup {...props} size='large' align={align} ref={forwardedRef}>
      {children}
    </ButtonGroup>
  )
})

Buttons.displayName = 'CenteredLayout.Buttons'

export function CenteredLayout ({
  children,
  displaySupportLink = false,
  verticallyCentered = false,
}: {
  children: React.ReactNode;
  displaySupportLink?: boolean;
  verticallyCentered?: boolean;
}) {
  return (
    <div className='max-w-[45rem] w-full mx-auto flex flex-col grow'>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={clsx({
          'flex flex-col justify-center grow': verticallyCentered,
          'pt-8 xs:pt-10': !verticallyCentered,
        })}
      >
        {children}
      </motion.div>
      {displaySupportLink && (
        <div className='mt-auto pt-10 text-center'>
          <SupportLink />
        </div>
      )}
    </div>
  )
}

CenteredLayout.Header = Header
CenteredLayout.Container = Container
CenteredLayout.Buttons = Buttons
